import { useContext, useMemo } from 'react';

import { AddRounded, CloseRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Icon,
  IconButton,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { useAssetTypesWithAssetsQuery } from '../../queries/assets';
import { useMetersQuery } from '../../queries/meters';
import { MetadataOptionsQuestion, MeterConfiguration, NumberQuestion } from '../../types';
import { Meter } from '../../types/asset';
import { QuestionInterface } from '../../types/oldWebForms';
import { SelectedAssets } from '../../types/questions';
import { DeleteIcon, PinOutlineIcon, TrendingUpOutlineIcon } from '../icons';
import { FormBuilderContext } from './FormBuilderProvider';

const customStyles: Record<string, SxProps<Theme>> = {
  configContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    border: '1px solid #DDE0E5',
    borderRadius: '16px',
    padding: '16px',
    paddingTop: '32px',
  },
  formControlText: {
    margin: '0px 0px 4px 0px',
  },
  select: {
    height: '34px',
  },
  menuItem: { fontSize: '12px' },
  addQuestionButton: {
    fontSize: '12px',
    lineHeight: '20px',
    alignSelf: 'flex-start',
    height: '24px',
    '& svg': {
      width: '14px',
      height: '14px',
    },
  },
  questionContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
  },
  formControlWrapper: {
    flex: 1,
  },
  closeButton: {
    padding: '4px',
    height: '34px',
    width: '34px',
  },
  menuIcon: {
    width: '20px',
    height: '20px',
    marginRight: '4px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  meterSettingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  deleteConfigButton: {
    marginTop: '-16px',
    padding: '3px',
    height: '24px',
    '& svg': {
      width: '18px',
      height: '18px',
    },
  },
  menuPaper: {
    maxHeight: '350px',
  },
  placeholder: {
    color: '#B8BABF',
  },
};

const numberFilterCallback =
  (questionId: string, usedQuestionIds: string[]) => (question: QuestionInterface) => {
    if (question.type !== 'number') return false;
    if (usedQuestionIds.includes(question.id)) return false;
    if (!('associatedMetadata' in question)) return true;
    return (question as NumberQuestion).associatedMetadata.questionId === questionId;
  };

const selectMetadataFilterCallback =
  (questionId: string, usedQuestionIds: string[]) => (question: QuestionInterface) => {
    if (question.type !== 'select_metadata') return false;
    if (question.id === questionId) return false;
    if (usedQuestionIds.includes(question.id)) return false;
    if (!('associatedMetadata' in question)) return true;
    return (
      (question as unknown as MetadataOptionsQuestion).associatedMetadata.questionId === questionId
    );
  };

export interface MeterConfigItemProps {
  questionId: string;
  configIndex: number;
  config: MeterConfiguration;
  tableId?: string;
  usedMeterIds: string[];
  usedQuestionIds: string[];
  canDelete: boolean;
  selectedAssets?: SelectedAssets;
  onDelete: () => void;
  onMeterChange: (newMeterId: string) => void;
  onAddQuestion: () => void;
  onUpdateQuestion: (slotIndex: number, value: string) => void;
  onDeleteQuestion: (slotIndex: number) => void;
  onCreateAssetQuestion: (
    tableId: string | undefined,
    type: 'number' | 'select_metadata',
    associationContext?: { parentQuestionId: string; configIndex: number; slotIndex: number },
  ) => void;
}

export function MeterConfigItem({
  questionId,
  configIndex,
  config,
  tableId,
  usedMeterIds,
  usedQuestionIds,
  canDelete,
  selectedAssets,
  onDelete,
  onMeterChange,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onCreateAssetQuestion,
}: MeterConfigItemProps) {
  const lang = useLanguage();
  const { questions } = useContext(FormBuilderContext);
  const { data: metersData } = useMetersQuery();
  const { data: assetTypesData } = useAssetTypesWithAssetsQuery('');
  const { checkFlag } = useFeatureFlags();

  const isLocationConfig = config.isLocation === true;
  const hasCustomSelection =
    (selectedAssets?.assets?.length ?? 0) > 0 || (selectedAssets?.asset_types?.length ?? 0) > 0;

  const filteredMeters = useMemo((): Meter[] => {
    const allMeters = metersData?.meters ?? [];
    if (!hasCustomSelection) return allMeters;

    const relevantAssetTypeIds = new Set<number>(
      (selectedAssets?.asset_types ?? []).map((id) => Number(id)),
    );

    if ((selectedAssets?.assets?.length ?? 0) > 0 && assetTypesData?.asset_types_with_assets) {
      const assetIdSet = new Set((selectedAssets.assets ?? []).map((id) => Number(id)));
      Object.values(assetTypesData.asset_types_with_assets).forEach((assetType) => {
        if (assetType.assets.some((a) => assetIdSet.has(a.id))) {
          relevantAssetTypeIds.add(assetType.id);
        }
      });
    }

    // Only show meters that exist in ALL selected asset types (intersection)
    if (relevantAssetTypeIds.size === 0) return [];

    const meterAssetTypeIds = (meter: Meter) =>
      new Set((meter.asset_type_meters ?? []).map((atm) => atm.asset_type_id));

    return allMeters.filter((meter) =>
      [...relevantAssetTypeIds].every((id) => meterAssetTypeIds(meter).has(id)),
    );
  }, [metersData?.meters, assetTypesData, selectedAssets, hasCustomSelection]);

  const availableNumberQuestions = tableId
    ? questions
        .find((q) => q.id === tableId)
        ?.questions.filter(numberFilterCallback(questionId, usedQuestionIds))
    : questions.filter(numberFilterCallback(questionId, usedQuestionIds));

  const availableSelectMetadataQuestions = tableId
    ? questions
        .find((q) => q.id === tableId)
        ?.questions.filter(selectMetadataFilterCallback(questionId, usedQuestionIds))
    : questions.filter(selectMetadataFilterCallback(questionId, usedQuestionIds));

  const availableQuestions = isLocationConfig
    ? availableSelectMetadataQuestions
    : availableNumberQuestions;

  const hasLocationConfig = usedMeterIds.includes('nestable_location');
  const options = [
    ...filteredMeters.map((meter) => ({
      id: meter.id,
      name: meter.name,
      icon: TrendingUpOutlineIcon,
    })),
    // Location is always valid, so include it in the list whenever we show the intersection (custom selection) or when location is enabled
    ...(checkFlag('NestableLocations') || isLocationConfig || hasCustomSelection
      ? [{ id: 'nestable_location', name: lang.assets.location, icon: PinOutlineIcon }]
      : []),
  ].filter(
    (opt) =>
      String(opt.id) === (isLocationConfig ? 'nestable_location' : config.meterId) ||
      (!usedMeterIds.includes(String(opt.id)) &&
        !(opt.id === 'nestable_location' && hasLocationConfig)),
  );

  const handleQuestionChange = (slotIndex: number, value: string) => {
    if (!value) return;
    onUpdateQuestion(slotIndex, value);
  };

  const currentMeterValue = isLocationConfig ? 'nestable_location' : config.meterId;

  return (
    <Box sx={customStyles.configContainer}>
      <FormControl>
        <Box sx={customStyles.meterSettingHeader}>
          <FormHelperText sx={customStyles.formControlText}>
            {lang.form_builder.selectData}
          </FormHelperText>
          {canDelete && (
            <IconButton
              onClick={onDelete}
              sx={customStyles.deleteConfigButton}
            >
              <DeleteIcon fill='#FF3F49' />
            </IconButton>
          )}
        </Box>
        <Select
          disableUnderline
          value={currentMeterValue ?? ''}
          sx={customStyles.select}
          onChange={(event) => onMeterChange(event.target.value)}
          MenuProps={{ sx: customStyles.menuPaper }}
        >
          {options.map((option, index) => (
            <MenuItem
              key={index}
              sx={customStyles.menuItem}
              value={String(option.id)}
            >
              <Box sx={customStyles.iconWrapper}>
                <Icon
                  component={option.icon}
                  sx={customStyles.menuIcon}
                  fill='#3E8FFA'
                />
                <Typography>{option.name}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {config.associatedQuestions?.length > 0 &&
        config.associatedQuestions.map((questionSlot, slotIndex) => (
          <Box
            key={`config-question-${slotIndex}`}
            sx={customStyles.questionContainer}
          >
            <FormControl sx={customStyles.formControlWrapper}>
              <FormHelperText sx={customStyles.formControlText}>
                {lang.form_builder.selectMeterQuestion}
              </FormHelperText>
              <Select
                disableUnderline
                displayEmpty
                value={questionSlot}
                sx={customStyles.select}
                onChange={(event) => handleQuestionChange(slotIndex, event.target.value)}
                MenuProps={{ sx: customStyles.menuPaper }}
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <Box
                        component='span'
                        sx={customStyles.placeholder}
                      >
                        {isLocationConfig
                          ? lang.form_builder.chooseSelectMetadataQuestion.replace(
                              '{select_metadata}',
                              lang.types.select_metadata,
                            )
                          : lang.form_builder.chooseNumberQuestion.replace(
                              '{number}',
                              lang.types.number,
                            )}
                      </Box>
                    );
                  }
                  const selected = (availableQuestions ?? []).find((q) => q.id === value);
                  return selected?.name ?? value;
                }}
              >
                {(availableQuestions ?? [])
                  .filter((availableQuestion) => {
                    const otherSelected = config.associatedQuestions.filter(
                      (_, i) => i !== slotIndex,
                    );
                    return (
                      availableQuestion.id === questionSlot ||
                      !otherSelected.includes(availableQuestion.id)
                    );
                  })
                  .map((availableQuestion) => (
                    <MenuItem
                      key={availableQuestion.id}
                      sx={customStyles.menuItem}
                      value={availableQuestion.id}
                    >
                      {availableQuestion.name}
                    </MenuItem>
                  ))}
                <MenuItem
                  onClick={() =>
                    onCreateAssetQuestion(
                      tableId,
                      isLocationConfig ? 'select_metadata' : 'number',
                      { parentQuestionId: questionId, configIndex, slotIndex },
                    )
                  }
                >
                  <Button
                    variant='text'
                    color='primary'
                    startIcon={<AddRounded />}
                    sx={{
                      ...customStyles.addQuestionButton,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  >
                    {isLocationConfig
                      ? lang.form_builder.createSelectMetadataQuestion
                      : lang.form_builder.createNumberQuestion}
                  </Button>
                </MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={() => onDeleteQuestion(slotIndex)}
              sx={customStyles.closeButton}
              size='small'
            >
              <CloseRounded fontSize='small' />
            </IconButton>
          </Box>
        ))}

      <Button
        variant='text'
        color='primary'
        startIcon={<AddRounded />}
        onClick={onAddQuestion}
        sx={customStyles.addQuestionButton}
      >
        {lang.form_builder.addMeterQuestion}
      </Button>
    </Box>
  );
}
